import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, forkJoin, map, Observable, of, throwError } from 'rxjs';
import { ApiService } from '@/core/services/open-api/api.service';
import { HttpClient } from '@angular/common/http';
import { BulkTileDeletionResult } from '@/core/models/bulk-tile-deletion-result';
import { Tile, type TileCreateRequestDto } from '@/core/models/tile';

/**
 * @class TileRepository
 * @description Encapsulates raw API communication for tiles.
 */
@Injectable({ providedIn: 'root' })
export class TileRepository extends ApiService {
    constructor(http: HttpClient) {
        super(http);
    }

    public checkTilesHaveActivities(tileIds: number[]): Observable<boolean> {
        return this._http
            .post<{ tilesHaveActivities: boolean }>(ApiService.openApiUrl + `tiles/activities-check`, {
                tileIds: tileIds,
            })
            .pipe(
                map((response: { tilesHaveActivities: boolean }) => {
                    if (response.tilesHaveActivities) {
                        return true;
                    }
                    return false;
                }),
                catchError((error) => {
                    if (error.status === 204) {
                        return of(false);
                    } else if (error.status === 404) {
                        console.error(`Tile has activities error.`);
                        return throwError(() => new Error('Tile not found'));
                    }
                    return throwError(() => new Error('Unexpected error checking tile activities'));
                }),
            );
    }

    public deleteTileById(id: number | undefined): Observable<void> {
        return this._http.delete<void>(ApiService.openApiUrl + 'tiles/' + id).pipe(
            catchError((error) => {
                console.error('Error deleting tiles:', error);
                throw error;
            }),
        );
    }

    // bulk tile deletion
    public bulkTileDeletion(tileIds: number[]): Observable<BulkTileDeletionResult> {
        return this._http
            .delete<BulkTileDeletionResult>(ApiService.openApiUrl + 'tiles/bulk', {
                body: { ids: tileIds },
            })
            .pipe(
                catchError((error) => {
                    console.error('Error deleting tiles:', error);
                    throw error;
                }),
            );
    }

    /**
     * Note: This method only exists for testing the new tile code and comparing it to the old legacy behavior.
     * TODO remove this method when code is live and stable
     * @deprecated Use getAllTiles() instead.
     */
    public getAllTilesLegacy(): Observable<Tile[]> {
        const tiles = this._http.get<Tile[]>(ApiService.openApiUrl + 'tiles');
        const archivedTiles = this._http.post<Tile[]>(ApiService.openApiUrl + 'tiles/filters', { archived: true });

        return forkJoin([tiles, archivedTiles]).pipe(
            map((result: [Tile[], Tile[]]) => {
                return [...(result[0] as Tile[]), ...(result[1] as Tile[])];
            }),
            // tap(t => this.tiles.next(t.sort((a, b) => a.name > b.name ? 1 : -1)))
        );
    }

    public getAllTiles(): Observable<Tile[]> {
        return this._http.get<Tile[]>(ApiService.openApiUrl + 'tiles?archived=all');
    }

    public getAllTilesAsync(): Promise<Tile[]> {
        return firstValueFrom(this.getAllTilesLegacy());
    }

    public getAssignedTiles(): Observable<Tile[]> {
        const tiles = this._http.get<Tile[]>(ApiService.openApiUrl + 'tiles/assignment/all');
        return tiles.pipe(
            map((response: Tile[]) => {
                return response as Tile[];
            }),
        );
    }

    public create(tile: Tile, assignedUserIds?: number[], assignedGroupIds?: number[]): Observable<Tile> {
        const request = {
            tile,
            assignedUserIds,
            assignedGroupIds,
        } satisfies TileCreateRequestDto;
        return this._http.post<Tile>(ApiService.openApiUrl + 'tiles', request).pipe(
            map((response: Tile) => {
                return response as Tile;
            }),
        );
    }

    public update(tile: Tile): Observable<Tile> {
        return this._http.put<Tile>(ApiService.openApiUrl + 'tiles/' + tile.id, tile).pipe(
            map((response) => {
                return response;
            }),
        );
    }
}
